import { DialogCreators } from "../utils/dialog-creators";
import { OrderType, MerchantProcessor, CustomizeItemUiOption } from "../types/enums";
import {
    IDataItem, IDataLoyaltyItem, IDataMerchant, IDataOrderTypeSubType, IDataPreModifier,
    IDataStore, IDataZipTax, IItemModifierDisplayGroupModifier, IItemOrderTypeSize, IItemPrice, IMenuItemPrice, IMenuItemTax
} from "../models/data.interface";
import { IOrderItem, IOrderItemModifier, IOrderItemTax } from "../models/order.interface";
import { Util } from "../utils/util";
import { ICalorie, IGetItemTaxDependencies, IGetItemTaxoItem } from "./interfaces/online-ordering.interface";
import { Data } from "../models/data";
import { Context } from "../models/context";
import { LocalizationService } from "../services/localization-service";
import { IUserRecentOrderItemModifier } from "@ts/models/user.interface";
import { IName } from "../interfaces/global.interfaces";

export class OnlineOrderingUtil {
    public static getOrderType(orderTypeID: number, orderTypeSubTypes: IDataOrderTypeSubType[]): IDataOrderTypeSubType | null {
        return orderTypeSubTypes.find(ot => ot.OrderTypeId === orderTypeID) || null;
    }

    public static GetOrderTypeName(orderTypeID: number, orderTypeSubTypes: IDataOrderTypeSubType[], localizationService: LocalizationService): string {
        const orderType = this.getOrderType(orderTypeID, orderTypeSubTypes);
        return orderType ? localizationService.GetName(orderType.Names) : "";
    }

    public static getWaitTimeByOrderType(data: Data, orderType: IDataOrderTypeSubType | undefined): number {
        if (!orderType)
            return 99;

        const tMinutes = Util.getNowStoreMinutes(data);
        const times = data.getProperty("WaitTimes");
        let waitTime = orderType.WaitTime;

        if (times.SET == 3 && times.WTIM) {
            const savedWaitTime = times.WTIM.find(x => x.MIN == (tMinutes - tMinutes % 15));

            if (savedWaitTime) {
                if (orderType.OrderTypeId === OrderType.DELIVERY && savedWaitTime.DELMIN && savedWaitTime.DELMIN > 0)
                    waitTime = savedWaitTime.DELMIN;
                else if (orderType.OrderTypeId !== OrderType.DELIVERY && savedWaitTime.MAKEMIN && savedWaitTime.MAKEMIN > 0)
                    waitTime = savedWaitTime.MAKEMIN;
            }
            else if (orderType.OrderTypeId === OrderType.DELIVERY)
                waitTime = times.DELMIN;
            else
                waitTime = times.INSMIN;
        }
        return waitTime;
    }

    public static IsOpenPay(merchandPids: IDataMerchant[]): boolean {
        return merchandPids.some(x =>
            x.ProcessorId === MerchantProcessor.OpenPay && x.TransactionType.toUpperCase().includes('E')
        ) ?? false;
    }

    public static GetItemSizes(mItem: IDataItem, orderTypeID: number): IItemOrderTypeSize[] {
        const orderType = mItem.OrderTypes.find(ot => ot.OrderTypeId === orderTypeID);
        return orderType ? orderType.Sizes : [];
    }

    // 2DO: Call Center logic?
    // public static CheckCustomerDistance(localizationService: LocalizationService, storeLat: number, storeLng: number, unitOfMeasurement: string, context: Context, YesCallBack: Function, NoCallBack: Function): void {
    //     let customerDistance = Util.Float2(Util.GetDistance(context.getProperty("location").latitude, context.getProperty("location").longitude, storeLat, storeLng, "M"));
    //     if (customerDistance > 5) {
    //         let messageName = "XtraMiles";

    //         if (unitOfMeasurement === "Metric") {
    //             // if (context.getProperty("storeInfo").Country.UnitsOfMeasurement == "Metric") {
    //             customerDistance = Util.Float2(Util.convertMilesToKilometers(customerDistance));
    //             messageName = "XtraKilometers";
    //         }

    //         DialogCreators.messageBox(localizationService.names(messageName).replace("??", `<div class='warning bold inline'>${customerDistance}</div>`), [
    //             { "text": localizationService.names("YesProceed"), "callBack": YesCallBack },
    //             { "text": localizationService.names("NoOtherStore"), "callBack": NoCallBack }]);
    //     } else
    //         YesCallBack();
    // }

    public static GetItemPrice(mItem: IDataItem, sizeId: number, modifiers: IOrderItemModifier[], orderPriceGroupId: number, defaultPriceGroupId: number, nonChargeableDisplayGroups: string[], preModifiers: IDataPreModifier[]): IMenuItemPrice {
        const iPrice = OnlineOrderingUtil.GetItemMenuPrice(mItem, sizeId, modifiers, orderPriceGroupId, defaultPriceGroupId, nonChargeableDisplayGroups, preModifiers);
        if (!mItem.Taxes || mItem.Taxes.length === 0) {
            iPrice.Tax = 0;
            return iPrice;
        }
        return iPrice;
    }

    public static GetItemMenuPrice(mItem: IDataItem, sizeId: number, modifiers: IOrderItemModifier[], orderPriceGroupId: number, defaultPriceGroupId: number, nonChargeableDisplayGroups: string[], preModifiers: IDataPreModifier[]): IMenuItemPrice {
        const iPrice = this._resolveItemPrice(mItem, sizeId, orderPriceGroupId, defaultPriceGroupId);
        const { mpCount, addToPrice, addOnMaxPriceCount } = this._calculateModifierTotals(mItem, sizeId, modifiers, iPrice, nonChargeableDisplayGroups, preModifiers);
        return this._buildFinalPrice(iPrice, mpCount, addToPrice, addOnMaxPriceCount);
    }

    private static _resolveItemPrice(mItem: IDataItem, sizeId: number, orderPriceGroupId: number, defaultPriceGroupId: number): IItemPrice {
        return mItem.Prices.find(x => x.PriceGroupId == orderPriceGroupId && x.SizeId == sizeId)
            ?? mItem.Prices.find(x => x.PriceGroupId == defaultPriceGroupId && x.SizeId == sizeId)
            ?? mItem.Prices.find(x => x.SizeId == sizeId)
            ?? {
                MinimumPrice: 0,
                MaximumPrice: 0,
                PriceGroupId: 0,
                PerModifierPrice: 0,
                Price: 0,
                SizeId: 0,
                TaxIncluded: false
            } as IItemPrice;
    }

    private static _calculateModifierTotals(
        mItem: IDataItem,
        sizeId: number,
        modifiers: IOrderItemModifier[],
        iPrice: IItemPrice,
        nonChargeableDisplayGroups: string[],
        preModifiers: IDataPreModifier[]
    ): { mpCount: number, addToPrice: number, addOnMaxPriceCount: number } {
        let mpCount = 0.00;
        let byItSelfChargeable = 0;
        let addToPrice = 0.00;
        let addOnMaxPriceCount = 0;
        const normalizedDisplayGroups = nonChargeableDisplayGroups ?? [];

        for (const iMod of modifiers) {
            const mod = this._resolveModifier(mItem, iMod);
            if (!mod) continue;

            const { pCount, preModPriceMultiplier } = this._resolvePreModCounts(iMod, mod, normalizedDisplayGroups, preModifiers);

            mpCount += pCount;
            addToPrice += this._applyModifierPricing(iMod, mod, sizeId, iPrice, pCount, preModPriceMultiplier);

            if (!mod.CountForPricing && mod.CountForPricingByItself)
                byItSelfChargeable += preModPriceMultiplier;

            if (preModPriceMultiplier > 1)
                addOnMaxPriceCount += preModPriceMultiplier - 1;
        }

        if (mpCount === 0 && byItSelfChargeable > 0)
            mpCount += 1;

        return { mpCount, addToPrice, addOnMaxPriceCount };
    }

    private static _resolveModifier(mItem: IDataItem, iMod: IOrderItemModifier): IItemModifierDisplayGroupModifier | null {
        const modDispGroup = mItem.ModifierDisplayGroups.find(x => x.ModifierDisplayGroupId == iMod.ModifierDisplayGroupId);
        if (!modDispGroup) return null;
        return modDispGroup.Modifiers.find(x => x.ModifierId == iMod.ModifierId) ?? null;
    }

    private static _resolvePreModCounts(
        iMod: IOrderItemModifier,
        mod: IItemModifierDisplayGroupModifier,
        nonChargeableDisplayGroups: string[],
        preModifiers: IDataPreModifier[]
    ): { pCount: number, preModPriceMultiplier: number } {
        const preMod = this.GetPreMod(iMod.PreModifierId, preModifiers);
        if (!preMod)
            return { pCount: 0, preModPriceMultiplier: 1.00 };

        let preModPriceMultiplier = Number(preMod.PriceCountMultiplier);

        if (nonChargeableDisplayGroups.indexOf(`-${iMod.ModifierDisplayGroupId}-`) !== -1)
            preModPriceMultiplier = 1;

        let pCount = 0;
        if (mod.CountForPricing)
            pCount = preModPriceMultiplier;
        else if (preModPriceMultiplier > 1)
            pCount = preModPriceMultiplier - 1;

        return { pCount: Math.max(0, pCount), preModPriceMultiplier };
    }

    private static _applyModifierPricing(
        iMod: IOrderItemModifier,
        mod: IItemModifierDisplayGroupModifier,
        sizeId: number,
        iPrice: IItemPrice,
        pCount: number,
        preModPriceMultiplier: number
    ): number {
        let addToPrice = 0.00;

        iMod.Price = pCount * Number(iPrice.PerModifierPrice);

        if (!mod.AddsToPrice)
            return addToPrice;

        const addToPriceMod = Number(preModPriceMultiplier * this.GetModifierAddToPrice(mod, sizeId));
        addToPrice += addToPriceMod;
        iMod.Price += addToPriceMod;


        return addToPrice;
    }

    private static _buildFinalPrice(iPrice: IItemPrice, mpCount: number, addToPrice: number, addOnMaxPriceCount: number): IMenuItemPrice {
        const minprice = iPrice.MinimumPrice ? iPrice.MinimumPrice + addToPrice : 0.00;
        let maxprice = iPrice.MaximumPrice ?? 0.00;

        const unroundedPrice = Number(iPrice.Price + (mpCount * iPrice.PerModifierPrice) + addToPrice);
        let price = Number((Math.round(unroundedPrice * 100) / 100).toFixed(2));

        const pricePerMod = Number(iPrice.PerModifierPrice);
        const mPrice = maxprice;

        if (maxprice < 0) maxprice = -maxprice;

        // Increase max price when user selected pre-modifier extra on a pizza that allows 1 topping
        if (maxprice > 0 && pricePerMod > 0 && addOnMaxPriceCount > 0)
            maxprice += pricePerMod * addOnMaxPriceCount;

        if (price < minprice)
            price = minprice;

        if (price > maxprice && mPrice < 0)
            price = mPrice;
        else if (price > maxprice && maxprice !== 0)
            price = maxprice;

        return { "Priced": (price > 0), "Price": Number(price), MinPrice: minprice, "TaxIncluded": iPrice.TaxIncluded, Tax: 0 };
    }

    public static GetPreMod(premodID: number, preModifiers: IDataPreModifier[]): IDataPreModifier | null {
        return preModifiers.find(pMod => pMod.PreModifierId === premodID) || null;
    }

    public static GetModifierAddToPrice(mod: IItemModifierDisplayGroupModifier, sizeID: number): number {
        const price = mod?.Prices.find(mPrice => mPrice.SizeId === sizeID) || null;
        return price ? price.AddOnPrice : 0.00;
    }

    public static TaxItem(mItem: IDataItem | null, oItem: IOrderItem, dependencies: IGetItemTaxDependencies): void {
        const oItemTax: IGetItemTaxoItem = {
            SizeId: oItem.SizeId,
            Quantity: oItem.Quantity,
            PriceIncludesTax: oItem.PriceIncludesTax,
            Price: oItem.Price,
            AfterTaxDiscount: oItem.AfterTaxDiscount,
            BeforeTaxDiscount: oItem.BeforeTaxDiscount
        };

        const taxData = this.GetItemTax(mItem, oItemTax, dependencies);
        oItem.TaxAmount = taxData.TaxableAmount;
        oItem.Taxes = taxData.Taxes;
        oItem.Tax = taxData.TotalTax;
    }

    public static GetItemTax(mItem: IDataItem | null, itemData: IGetItemTaxoItem, dependencies: IGetItemTaxDependencies): IMenuItemTax {
        const { taxesData, zipTaxesData, isCallCenter, orderTypeId, address } = dependencies;
        let taxed = false;
        let totalTax = 0;
        const taxes: IOrderItemTax[] = [];
        let taxTaxableAmount = 0.00;
        let itemTaxableAmount = 0.00;
        if (mItem?.Taxes) {
            for (const mItemTax of mItem.Taxes) {
                const tax = taxesData.find(x => x.TaxId === mItemTax.TaxId);
                if (!tax)
                    continue;

                let taxAmount = Util.Float5(tax.FixAmount);
                let taxRate = Util.Float5(tax.Rate);
                let taxSizeID = tax.SizeId;

                // 2DO: Refactor when Call Center added
                // if (isCallCenter) {
                //     const storeTax = this.GetCallCenterStoreTaxRate(tax.TaxId, aOLO.Order.StoreID, aOLO.data.Stores);
                //     if (storeTax) {
                //         taxAmount = storeTax.taxAmount;
                //         taxRate = storeTax.taxRate;
                //         taxSizeID = storeTax.sizeID;
                //     }
                // }

                if (orderTypeId === OrderType.DELIVERY && address) {
                    const zip = address.Zip;
                    const zipTax = this.GetZipTaxes(tax.TaxId, zip, zipTaxesData);
                    if (zipTax !== null) {
                        taxAmount = Util.Float5(zipTax.FixAmount);
                        taxRate = Util.Float5(zipTax.Rate);
                        taxSizeID = zipTax.SizeId;
                    }
                }

                const iTax = {
                    TaxId: 0,
                    IsPerOrder: false,
                    DisplayNextToItem: false,
                    Amount: 0.0,
                    TaxTypeId: 0,
                    TaxTypeName: []
                } as IOrderItemTax;

                let iPush = false;
                if ((tax.IsSizeBase || tax.IsFixedAmountBase) && (taxSizeID === itemData.SizeId || taxSizeID == 0)) {
                    // if the tax is next to the Item round it to 2 decimal point.
                    iTax.Amount = Util.Float2(taxAmount * itemData.Quantity);
                    iPush = true;
                } else if (tax.IsPercentageBase) {
                    let oItemPrice = itemData.Price;
                    if (itemData.PriceIncludesTax && itemData.AfterTaxDiscount > 0)
                        oItemPrice = Util.Float2(((oItemPrice - Util.Float2(itemData.AfterTaxDiscount / itemData.Quantity)) / (1 + taxRate / 100)));
                    else if (itemData.PriceIncludesTax)
                        oItemPrice = Util.Float2((oItemPrice / (1 + taxRate / 100)));

                    itemTaxableAmount = Util.Float2((oItemPrice * itemData.Quantity) + taxTaxableAmount);

                    if (tax.IsAffectedByDiscounts)
                        itemTaxableAmount = Util.Float2(itemTaxableAmount - itemData.BeforeTaxDiscount);

                    if (itemTaxableAmount < 0)
                        itemTaxableAmount = 0.00;

                    if (itemData.PriceIncludesTax) {
                        const itemPriceMinusDiscount = itemData.Price - Util.Float2(itemData.AfterTaxDiscount / itemData.Quantity);
                        iTax.Amount = Util.Float5((itemPriceMinusDiscount * itemData.Quantity) - itemTaxableAmount);
                    } else
                        iTax.Amount = Util.Float5(Util.Float2(itemTaxableAmount) * (Util.Float5(taxRate) / 100));

                    iPush = true;
                }

                if (iPush) {
                    if (tax.IsAddToSalesAmount)
                        taxTaxableAmount += Util.Float2(iTax.Amount);

                    iTax.IsPerOrder = tax.IsPerOrder;
                    iTax.DisplayNextToItem = tax.IsNextToItemIndividually;
                    iTax.TaxTypeId = tax.TaxTypeId;
                    iTax.TaxId = tax.TaxId;
                    iTax.TaxTypeName = tax.Names;
                    taxes.push(iTax);
                }
            }
            taxes.forEach((x) => {
                totalTax += x.Amount;
            });
            taxed = true;
        }

        return {
            Taxed: taxed,
            Taxes: taxes,
            TaxableAmount: itemTaxableAmount,
            TotalTax: Util.Float2(totalTax)
        }
    }

    public static GetZipTaxes(taxID: number, zipCode: string, zipTaxes: IDataZipTax[]): IDataZipTax | null {
        return zipTaxes.find(zTax => zTax.TaxId === taxID && zTax.ZipCode === zipCode) || null;
    }

    public static IsRedeemItemChargeable(itemID: number, sizeID: number, loyaltyItems: IDataLoyaltyItem[]): boolean {
        if (loyaltyItems.length === 0)
            return false;

        const item = loyaltyItems.find(x => x.ItemId === itemID && x.SizeId === sizeID);
        return (item) ? item.IsChargeable : false;
    }

    public static GetRedeemItemChargeablePrice(item: IOrderItem, loyaltyItems: IDataLoyaltyItem[]): number {
        const isChargeable = this.IsRedeemItemChargeable(item.ItemId, item.SizeId, loyaltyItems);
        const itemPrice = isChargeable ? item.MinPrice : item.Price;
        return itemPrice;
    }

    public static GetRedeemItemChargeableHalfPrice(itemKey: number, orderItems: IOrderItem[], loyaltyItems: IDataLoyaltyItem[]): number {
        let price = 0;
        const oItems = orderItems.filter(x => x.ItemKey === itemKey);
        for (const item of oItems) {
            const isChargeable = this.IsRedeemItemChargeable(item.ItemId, item.SizeId, loyaltyItems);
            const itemPrice = isChargeable ? Math.round(item.HalfHalfPrice / item.HalfCount) : item.Price;
            price += itemPrice;
        }

        return price;
    }

    public static GetItemSize(mItem: IDataItem, orderTypeID: number, sizeID: number): IItemOrderTypeSize | null {
        const sizes = this.GetItemSizes(mItem, orderTypeID);
        return sizes?.find(size => size.SizeId === sizeID) || null;
    }

    public static ItemHasSize(mItem: IDataItem, orderTypeID: number, sizeID: number): boolean {
        return (this.GetItemSize(mItem, orderTypeID, sizeID) != null);
    }

    public static GetLoyaltyPoints(itemID: number, sizeID: number, loyaltyItems: IDataLoyaltyItem[]): number | null {
        if (loyaltyItems.length === 0)
            return null;

        const points = loyaltyItems.filter(x => x.ItemId === itemID && x.SizeId === sizeID).map(x => x.Points);
        return (points.length == 1) ? points[0] : null;
    }

    public static GetItemDefaultSize(mItem: IDataItem, orderTypeID: number, sizeID: number | null, dszID: number): IItemOrderTypeSize {
        const sizes = this.GetItemSizes(mItem, orderTypeID);
        const iDefSize = sizes.find(siz => siz.IsDefault) || sizes.find(siz => siz.SizeId === dszID) || sizes.find(siz => siz.SizeId === sizeID) || sizes[0];
        return iDefSize;
    }

    public static getMenuItemModifiersCalories(mItem: IDataItem, sizeID: number | null, defPreModID: number): number {
        if (!mItem.ModifierDisplayGroups)
            return 0;

        const disGrpModCount: number[] = [];
        let totalCals = 0;

        for (const modDG of mItem.ModifierDisplayGroups) {
            for (const mod of modDG.Modifiers) {
                if (!mod.IsDefault)
                    continue;

                const modCount: number = (disGrpModCount[mod.MergedModifierDisplayGroupId] || this.getMenuItemModCount(mItem, mod.MergedModifierDisplayGroupId));
                disGrpModCount[mod.MergedModifierDisplayGroupId] = modCount;
                totalCals += Math.ceil(this.getMenuItemModCalories(mod, sizeID, modCount, defPreModID, 0, [], 0));
            }
        }

        return totalCals;
    }

    public static getMenuItemModCount(mItem: IDataItem, mergedMDGID: number): number {
        let count = 0;
        for (const modDG of mItem.ModifierDisplayGroups) {
            for (const mod of modDG.Modifiers) {
                if (mod.IsDefault && mod.MergedModifierDisplayGroupId == mergedMDGID)
                    count++;
            }
        }
        return count;
    }

    public static getMenuItemModCalories(mod: IItemModifierDisplayGroupModifier, sizeID: number | null, modCount: number, preModId: number, setting: number, premodifiers: IDataPreModifier[], defaultPreModifierId: number): number {
        if (!mod?.Calories)
            return 0;

        if (setting === 1) // setting 1 is multiplier, setting 2 is variable
            return this._getCaloriesForSettingMultiplier(mod.Calories, sizeID, modCount, preModId, premodifiers, defaultPreModifierId);

        return this._getCaloriesForSettingVariable(mod.Calories, sizeID, modCount, preModId);
    }


    private static _getCaloriesForSettingMultiplier(calories: IItemModifierDisplayGroupModifier['Calories'], sizeID: number | null, modCount: number, preModId: number, premodifiers: IDataPreModifier[], defaultPreModifierId: number): number {
        const modcals = this._findModCals(calories, sizeID, modCount, defaultPreModifierId);
        if (modcals.length === 0)
            return 0;

        const sum = modcals.reduce((total, cal) => total + cal.Calories, 0);
        if (preModId === defaultPreModifierId)
            return sum;

        const premod = this.GetPreMod(preModId, premodifiers);
        return sum * (premod?.InventoryMultiplier ?? 1);
    }

    private static _findModCals(calories: IItemModifierDisplayGroupModifier['Calories'], sizeID: number | null, modCount: number, defaultPreModifierId: number): IItemModifierDisplayGroupModifier['Calories'] {
        let result = calories.filter(x => x.SizeId === sizeID && x.PreModifierId === defaultPreModifierId && x.ModifierCount === modCount);
        if (result.length > 0) return result;

        result = calories.filter(x => x.SizeId === sizeID && x.ModifierCount === modCount);
        if (result.length > 0 || modCount === 0) return result;

        const maxCount = Math.max(...Array.from(calories, o => o.ModifierCount));
        result = calories.filter(x => x.SizeId === sizeID && x.PreModifierId === defaultPreModifierId && x.ModifierCount === maxCount);
        if (result.length > 0) return result;

        return calories.filter(x => x.SizeId === sizeID && x.ModifierCount === maxCount);
    }

    private static _getCaloriesForSettingVariable(calories: IItemModifierDisplayGroupModifier['Calories'], sizeID: number | null, modCount: number, preModId: number): number {
        for (let i = modCount; i >= 0; i--) {
            const modPremodCals = calories.filter(x => x.SizeId === sizeID && x.PreModifierId === preModId && x.ModifierCount === i);
            if (modPremodCals.length > 0)
                return modPremodCals.reduce((sum, cal) => sum + cal.Calories, 0);
        }
        return 0;
    }

    public static getOrderItemModifiersCalories(mItem: IDataItem, modifiers: IOrderItemModifier[], sizeID: number | null, defaultPreModifierId: number): number {
        const disGrpModCount: number[] = [];
        let totalCals = 0;
        for (const oMod of modifiers) {
            let modCount = 0;
            if (disGrpModCount[oMod.MergedModifierDisplayGroupId])
                modCount = disGrpModCount[oMod.MergedModifierDisplayGroupId];
            else {
                disGrpModCount[oMod.MergedModifierDisplayGroupId] = this.GetModCount(modifiers, oMod.MergedModifierDisplayGroupId, 0);
                modCount = disGrpModCount[oMod.MergedModifierDisplayGroupId];
            }

            const modDispGroup = mItem.ModifierDisplayGroups.find(x => x.ModifierDisplayGroupId == oMod.ModifierDisplayGroupId);
            if (!modDispGroup)
                continue;

            const mod = modDispGroup.Modifiers.find(x => x.ModifierId == oMod.ModifierId);
            if (!mod)
                continue;

            totalCals += Math.ceil(this.getMenuItemModCalories(mod, sizeID, modCount, oMod.PreModifierId, 0, [], defaultPreModifierId));
        }
        return totalCals;
    }

    public static GetModCount(mods: IOrderItemModifier[], mergedModifierDisplayGroupId: number, nonePreModifierId: number): number {
        let count = 0;
        mods.forEach(mod => {
            if (mod.PreModifierId !== nonePreModifierId && mod.MergedModifierDisplayGroupId == mergedModifierDisplayGroupId)
                count += 1;
        });
        return count;
    }

    public static getItemSizeMinMaxCalories(mItem: IDataItem, sizeID: number | null): { minCal: number, maxCal: number, serves: number } {
        const cal = {
            minCal: 1000000,
            maxCal: 0,
            serves: 1
        };
        if (mItem.SizeCalories) {
            const calSize = mItem.SizeCalories.find(x => x.SizeId === sizeID);
            if (calSize) {
                cal.serves = calSize.Servings;
                if (cal.minCal > calSize.MinimumCalories)
                    cal.minCal = calSize.MinimumCalories;

                if (calSize.MaximumCalories && cal.minCal > calSize.MaximumCalories)
                    cal.minCal = calSize.MaximumCalories;

                if (cal.maxCal < calSize.MinimumCalories)
                    cal.maxCal = calSize.MinimumCalories;

                if (calSize.MaximumCalories && cal.maxCal < calSize.MaximumCalories)
                    cal.maxCal = calSize.MaximumCalories;
            }
        }

        if (cal.minCal == 1000000)
            cal.minCal = 0;

        return cal;
    }

    public static isModOfferedOnSize(iMod: IItemModifierDisplayGroupModifier, sizeID: number): boolean {
        const modAddPrice = this.GetModifierAddToPrice(iMod, sizeID);
        return (modAddPrice !== -1);
    }

    public static PriceItem(data: Data, orderItems: IOrderItem[], oItem: IOrderItem, dependencies: IGetItemTaxDependencies, orderTypePriceGroupId: number): boolean {
        const defaultPriceGroup = data.getProperty("DefaultPriceGroupId");
        const nonChargeableDisplayGroups = data.getProperty("Settings").NCDG;
        const preModifiers = data.getProperty("PreModifiers");
        const items = data.getProperty("Items");
        let priced = false;
        const mItem = items.find(x => x.ItemId === oItem.ItemId) || null;
        if (mItem?.Prices) {
            let price1 = this.GetItemPrice(mItem, oItem.SizeId, oItem.Modifiers, orderTypePriceGroupId, defaultPriceGroup, nonChargeableDisplayGroups, preModifiers);
            if (price1.Price < 0)
                price1.Price = -price1.Price;

            oItem.MenuPrice = price1.Price;
            oItem.MenuPriceTax = price1.Tax;
            oItem.PriceIncludesTax = price1.TaxIncluded
            oItem.Price = price1.Price;
            oItem.MinPrice = price1.MinPrice;
            oItem.HalfHalfPrice = price1.Price;

            if (oItem.HalfCount === 2) {
                let hhPrice = price1.Price;
                let hhMinPrice = price1.MinPrice;
                let hhPriceTax = price1.Tax;
                const oItem2 = this.GetOtherHalfItem(oItem.ItemKey, Math.round(((oItem.HalfIndex % 2) + 1)), orderItems);
                if (oItem2 !== null) {
                    const mItem2 = items.find(x => x.ItemId === oItem2?.ItemId);
                    if (mItem2?.Prices) {
                        let price2 = this.GetItemPrice(mItem2, oItem2.SizeId, oItem2.Modifiers, orderTypePriceGroupId, defaultPriceGroup, nonChargeableDisplayGroups, preModifiers);
                        if (price2.Price < 0) price2.Price = -price2.Price;
                        const highestPrice = data.getProperty("Settings").HHP === 1;
                        if (highestPrice) {
                            hhPrice = Util.Float2(Math.max(price1.Price, price2.Price));
                            hhMinPrice = Util.Float2(Math.max(price1.MinPrice, price2.MinPrice));
                            hhPriceTax = Util.Float2(Math.max(price1.Tax, price2.Tax));
                        } else {
                            hhPrice = Util.Float2((price1.Price + price2.Price) / 2);
                            hhMinPrice = Util.Float2((price1.MinPrice + price2.MinPrice) / 2);
                            hhPriceTax = Util.Float2((price1.Tax + price2.Tax) / 2);
                        }

                        oItem.Price = Util.Float2(hhPrice / 2);
                        oItem.MinPrice = Util.Float2(hhMinPrice / 2);
                        oItem.Tax = Util.Float2(hhPriceTax / 2);
                        oItem.HalfHalfPrice = hhPrice;

                        oItem2.Price = Util.Float2(hhPrice / 2);
                        oItem2.MinPrice = Util.Float2(hhMinPrice / 2);
                        oItem2.Tax = Util.Float2(hhPriceTax / 2);
                        oItem2.HalfHalfPrice = hhPrice;

                        const totalAssignedPrice = oItem.Price + oItem2.Price;
                        if (totalAssignedPrice !== hhPrice) {
                            const diff = Util.Float2(hhPrice - totalAssignedPrice);
                            oItem2.Price = Util.Float2(oItem.Price + diff);
                        }

                        this.TaxItem(mItem2, oItem2, dependencies);
                    }
                }
            }
            priced = true;
        } else {
            oItem.MenuPrice = 0;
            oItem.MenuPriceTax = 0;
            oItem.Tax = 0;
            oItem.PriceIncludesTax = false;
            oItem.Price = 0;
            oItem.MinPrice = 0;
            oItem.HalfHalfPrice = 0;
        }
        this.TaxItem(mItem, oItem, dependencies);
        return priced;
    }

    public static GetOtherHalfItem(itemKey: number, halfIndex: number, orderItems: IOrderItem[]): IOrderItem | null {
        return orderItems.find(oItem => oItem.ItemKey === itemKey && oItem.HalfCount === 2 && oItem.HalfIndex === halfIndex) || null;
    }

    public static getStoreWaitTime(data: Data, store: IDataStore, orderTypeID: number): number {
        const tMinutes = Util.getNowStoreMinutes(data);
        const savedWaitTime = store.AutoWaitTimes?.find(x => x.MIN === (tMinutes - tMinutes % 15));
        if (savedWaitTime) {
            return (orderTypeID === 3) ? savedWaitTime.DELMIN : savedWaitTime.MAKEMIN;
        }
        const waitTime = store.WaitTimes?.find(x => x.OrderTypeId === orderTypeID);
        return waitTime?.WaitTime ?? 0;
    }

    // 2DO: Maybe dont need?
    // public static resetTimeFulfillmentTimeExpired(localAOLO: IaOLO): void {
    //     DialogCreators.messageBoxOk(Names("FulfillmentTime").replace("???", OnlineOrderingUtil.GetOrderTypeName(localAOLO.Order.OrderTypeID, localAOLO.data.OrderTypeSubTypes)).replace("??", localAOLO.Order.OrderTypeSubType.WaitTime.toString()), localAOLO.buttonHoverStyle);
    //     localAOLO.Order.FutureDate = null;
    //     localAOLO.Order.FutureMinute = -1;
    //     localAOLO.Order.PrintTime = Util.formatDateTimeSeconds(Util.NowStore(localAOLO.Temp.TimeOffset));
    //     localAOLO.Order.PromiseTime = Util.formatDateTimeSeconds(Util.DateAdd(Util.NowStore(localAOLO.Temp.TimeOffset), localAOLO.Order.OrderTypeSubType.WaitTime, "m"));
    //     // Refactor??: if (localAOLO.Dialog.OrderType)
    //     //     localAOLO.Dialog.OrderType.handleOrderTypeOrderTimeChange(false, false);
    //     //Refactor: this.GUI_SetOrder(localAOLO, localAOLOModules);
    // }

    public static redeemPointsAsync = async (points: number | null, itemKey: number): Promise<void> => {
        // 2DO: Logic for redeem points. Do later
        //const item = this._order.GetItemByItemKey(itemKey);
        //if (!item)
        //    return;

        //const isHalfHalf = item.HalfCount > 1;

        //if (item.IsRedeemed) {
        //    for (const [index, item] of localAolo.Temp.loyaltyPointCoupons.entries()) {
        //        if (item.itemKey === itemKey) {
        //            points = item.points;
        //            localAolo.Temp.loyaltyPointCoupons.splice(index, 1);
        //        }
        //    }
        //    localAolo.Temp.loyaltyPointsRedeemed -= points ?? 0;
        //    if (localAolo.User.LoyaltyData)
        //        localAolo.User.LoyaltyData.CurrentPoints = localAolo.User.LoyaltyData.CurrentPoints + (points ?? 0);

        //    if (isHalfHalf) {
        //        const halfItems = localAolo.Order.Items.filter(x => x.ItemKey == item.ItemKey); // Refactor: Use order.getOrderItemsByItemKey()
        //        for (const halfItem of halfItems) {
        //            halfItem.IsRedeemed = false;
        //        }
        //    } else
        //        item.IsRedeemed = false;

        //    Util.showElement(`btn_cart_item_redeem_${itemKey}`);
        //} else {
        //    Util.hideElement(`btn_cart_item_redeem_${itemKey}`);
        //    localAolo.Temp.loyaltyPointsRedeemed += points || 0;
        //    if (localAolo.User.LoyaltyData)
        //        localAolo.User.LoyaltyData.CurrentPoints = localAolo.User.LoyaltyData.CurrentPoints - (points ?? 0);

        //    if (isHalfHalf) {
        //        const halfItems = localAolo.Order.Items.filter(x => x.ItemKey == item.ItemKey); // Refactor: Use order.getOrderItemsByItemKey()
        //        for (const halfItem of halfItems) {
        //            halfItem.IsRedeemed = true;
        //        }
        //    } else
        //        item.IsRedeemed = true;

        //    const currentDiscountsApplied = localAolo.Order.ItemsCoupons.filter(x => x.ItemKey === itemKey).reduce((acc, current) => acc + current.AfterTaxDiscount + current.BeforeTaxDiscount, 0);
        //    const loyaltyItems = localAolo.data.LoyaltyItems;
        //    const price = isHalfHalf ? this.getRedeemItemChargeableHalfPrice(item.ItemKey, localAolo.Order.Items, loyaltyItems) : this.getRedeemItemChargeablePrice(item, loyaltyItems);
        //    const discountAmount = price - currentDiscountsApplied;
        //    const params: IApplyCouponByIdParams = {
        //        couponId: 7,
        //        discount: discountAmount,
        //        couponCode: (points ? points.toString() : null),
        //        itemKey: itemKey
        //    };
        //    await localAolo.Modules.Coupon.ApplyCouponByID(params);
        //    const couponKey = localAolo.Order.Coupons.find(x => x.CouponId == 7 && x.Discount == discountAmount);
        //    localAolo.Temp.loyaltyPointCoupons.push({ itemKey: itemKey, points: points, couponKey: couponKey?.CouponKey });
        //}

        //Util.setElement("innerText", "spn_cart_points_available", `${localAolo.User.LoyaltyData?.CurrentPoints || 0} `);
    }

    /**
     * Determines if an item is valid for inclusion.
     * 
     * @param {any} item - The item to validate.
     * @returns {boolean} Whether the item is valid.
     */
    public static _isItemValid(item: IDataItem, orderTypeId: number, isQrCode: boolean): boolean {
        let itemSizes = OnlineOrderingUtil.GetItemSizes(item, orderTypeId);

        if (isQrCode) {
            itemSizes = itemSizes.filter(size => size.IsOfferedQR);
        }

        return itemSizes.length > 0;
    }

    /**
     * Checks if a menu item is valid for a given order type.
     * @param menuItem - The menu item being checked.
     * @param orderTypeId - The order type ID.
     * @returns True if the item is valid for the order type, false otherwise.
     */
    public static isItemValidForOrderType(menuItem: IDataItem, orderTypeId: number): boolean {
        return menuItem.OrderTypes.some(orderType => orderType.OrderTypeId === orderTypeId);
    }

    public static checkAgeAndCustomizeItem(context: Context, localizationService: LocalizationService, mItem: IDataItem, func: Function): void {
        if (mItem.MinimumCustomerAge && mItem.MinimumCustomerAge > 0 && mItem.MinimumCustomerAge > context.getProperty("age")) {
            DialogCreators.messageBox(localizationService.names("AGE").replace("??", mItem.MinimumCustomerAge.toString()), [
                {
                    "text": localizationService.names("YesIm"), "callBack": () => {
                        context.setProperty("age", mItem.MinimumCustomerAge);
                        func();
                    }
                },
                { "text": localizationService.names("NoImNot"), "callBack": null }]);
        } else
            func();
    }

    /**
     * Converts an item modifier to a displayable format.
     * @param {IUserRecentOrderItemModifier} itemModifier - The item modifier to convert.
     * @returns {object} - The converted item modifier.
     */
    public static _mapModifierForDisplay(itemModifier: IUserRecentOrderItemModifier): { modifierId: number, halfIndex: number, preModifierId: number, isDefault: boolean } {
        return {
            modifierId: itemModifier.modifierId,
            halfIndex: itemModifier.halfIndex,
            preModifierId: itemModifier.preModifierId,
            isDefault: itemModifier.isDefault
        };
    }

    /**
     * Converts an array of order items to a displayable format for modifiers.
     * @param {IOrderItem[]} OrderItem - The order items to convert.
     * @returns {object[]} - The converted item modifiers.
     */
    public static _collectModifiersForDisplay(OrderItem: IOrderItem[]): { modifierId: number, displayGroupId: number, halfIndex: number, preModifierId: number, isDefault: boolean, isCrust: boolean }[] {
        let modifiersToDisplay = OrderItem[0].Modifiers.map(m => {
            return {
                modifierId: m.ModifierId,
                displayGroupId: m.ModifierDisplayGroupId,
                halfIndex: OrderItem[0].HalfIndex,
                preModifierId: m.PreModifierId,
                isDefault: m.IsDefault,
                isCrust: m.IsCrust
            };
        });

        if (OrderItem.length > 1) {
            modifiersToDisplay = modifiersToDisplay.concat(
                OrderItem[1].Modifiers.map(m => {
                    return {
                        modifierId: m.ModifierId,
                        displayGroupId: m.ModifierDisplayGroupId,
                        halfIndex: OrderItem[1].HalfIndex,
                        preModifierId: m.PreModifierId,
                        isDefault: m.IsDefault,
                        isCrust: m.IsCrust
                    };
                }));
        }
        return modifiersToDisplay;
    }
    /**
     * Generates the HTML for item modifiers.
     * @param {boolean} isHalfHalf - Indicates if the item is a half-and-half item.
     * @param {{ modifierId: number, halfIndex: number, preModifierId: number, isDefault: boolean }[]} modifiers - Item Modifiers data
     * @param {Data} data - Shared data object.
     * @param {Context} context - Context object.
     * @returns {string} - The HTML for the item modifiers.
     */
    public static generateModifiersText(isHalfHalf: boolean, modifiers: { modifierId: number, halfIndex: number, preModifierId: number, isDefault: boolean }[], localizationService: LocalizationService, data: Data): string {
        const modifiersText = this.generateItemModifiers(modifiers, data, localizationService);

        if (isHalfHalf) {
            return `
                ${modifiersText.leftModifiers ? `<p class="menu-item-cart-card-modifiers"><strong>${localizationService.names("Left")}:</strong> ${modifiersText.leftModifiers}</p>` : ""}
                ${modifiersText.rightModifiers ? `<p class="menu-item-cart-card-modifiers"><strong>${localizationService.names("Right")}:</strong> ${modifiersText.rightModifiers}</p>` : ""}
                ${modifiersText.wholeModifiers ? `<p class="menu-item-cart-card-modifiers"><strong>${localizationService.names("Whole")}:</strong> ${modifiersText.wholeModifiers}</p>` : ""}
            `;
        } else {
            return `<p class="menu-item-cart-card-modifiers">${modifiersText.wholeModifiers}</p>`;
        }
    }

    /**
     * Generates a list of modifiers categorized into whole, left, and right modifiers for a given set of order items.
     * @param {Array} modifiers - The list of item modifiers.
     * @param {Data} data - Shared data object.
     * @param {Context} context - Context object.
     * @returns {{ wholeModifiers: string, leftModifiers: string, rightModifiers: string }} - An object containing formatted strings of whole, left, and right modifiers.
     */
    public static generateItemModifiers(
        modifiers: {
            modifierId: number,
            halfIndex: number,
            preModifierId: number,
            isDefault: boolean
        }[],
        data: Data,
        localizationService: LocalizationService
    ): {
        wholeModifiers: string,
        leftModifiers: string,
        rightModifiers: string
    } {
        const defaultPreModifierId = data.getProperty("DefaultPreModifierId");
        const nonePreModifierId = data.getProperty("NonePreModifierId");
        const modifiersData = data.getProperty("Modifiers");

        const leftModifiers: string[] = [];
        const rightModifiers: string[] = [];
        const wholeModifiers: string[] = [];

        // Precompute modifier counts to avoid repeated .filter()
        const modifierCounts = modifiers.reduce<Record<number, number>>((acc, m) => {
            if (m.preModifierId !== nonePreModifierId) {
                acc[m.modifierId] = (acc[m.modifierId] ?? 0) + 1;
            }
            return acc;
        }, {});

        for (const item of modifiers) {
            const modifier = modifiersData.find(x => x.ModifierId === item.modifierId);
            if (!modifier)
                continue;

            const isSingleSelect = data.getProperty("Settings").OOS.customizeItemMode === CustomizeItemUiOption.SINGLE_ITEM;
            const isWhole = item.halfIndex === 0 || (isSingleSelect && (modifierCounts[item.modifierId] ?? 0) > 1);
            const modifierName = localizationService.GetName(modifier.Names);
            const prefix = this.getModifierPrefix(item.preModifierId, data, localizationService);


            if (!OnlineOrderingUtil.shouldIncludeModifier(item, isWhole, defaultPreModifierId))
                continue;

            const formattedModifier = `${prefix} <span ltagj="${Util.toLtagj(modifier.Names)}">${modifierName}</span>`;

            if (isWhole && item.halfIndex !== 2) {
                wholeModifiers.push(formattedModifier);
            } else if (!isWhole && ((!item.isDefault || (item.isDefault && item.preModifierId === nonePreModifierId)) || item.preModifierId !== defaultPreModifierId)) {
                if (item.halfIndex !== 2)
                    leftModifiers.push(formattedModifier);
                else
                    rightModifiers.push(formattedModifier);
            }
        }

        return {
            wholeModifiers: wholeModifiers.join(" \u2022 "),
            leftModifiers: leftModifiers.join(" \u2022 "),
            rightModifiers: rightModifiers.join(" \u2022 ")
        };
    }

    /**
     * Generates a list of modifiers categorized into whole, left, and right modifiers for a given set of order items.
     * @param {Array} modifiers - The list of item modifiers.
     * @param {Data} data - Shared data object.
     * @param {Context} context - Context object.
     * @returns {{ wholeModifiers: string, leftModifiers: string, rightModifiers: string }} - An object containing formatted strings of whole, left, and right modifiers.
     */
    public static generateItemModifiersNew(
        modifiers: {
            modifierId: number,
            displayGroupId: number
            halfIndex: number,
            preModifierId: number,
            isDefault: boolean
        }[],
        data: Data,
        localizationService: LocalizationService
    ): {
        left: { default: string[]; nonDefault: string[] };
        right: { default: string[]; nonDefault: string[] };
    } {
        const defaultPreModifierId = data.getProperty("DefaultPreModifierId");
        const modifiersData = data.getProperty("Modifiers");
        const allDisplayGroups = data.getProperty("ModifierDisplayGroups");

        const result = {
            left: { default: [] as string[], nonDefault: [] as string[] },
            right: { default: [] as string[], nonDefault: [] as string[] }
        };

        const isNonDefaultPreMod = (id: number) =>
            id !== defaultPreModifierId;

        const getSide = (halfIndex: number) =>
            halfIndex === 2 ? result.right : result.left;

        for (const item of modifiers) {
            const modifier = modifiersData.find(x => x.ModifierId === item.modifierId);
            if (!modifier)
                continue;

            const displayGroup = allDisplayGroups.find(x => x.ModifierDisplayGroupId === item.displayGroupId);
            if (!displayGroup?.IsHalfable)
                continue

            const side = getSide(item.halfIndex);
            const name = localizationService.GetName(modifier.Names).trim();

            const prefix = isNonDefaultPreMod(item.preModifierId)
                ? this.getModifierPrefix(item.preModifierId, data, localizationService).trim()
                : "";

            const label = prefix ? `${prefix} ${name}` : name;

            if (item.isDefault && item.preModifierId === defaultPreModifierId) {
                side.default.push(name);
                continue;
            }

            side.nonDefault.push(label);
        }

        return result;
    }

    /**
     * Determines whether a modifier should be included in the output list
     * based on its preModifierId, default status, and whether it applies
     * to the whole item or just a half.
     *
     * @param {{modifierId: number, halfIndex: number, preModifierId: number, isDefault: boolean}} item 
     *        The modifier instance being evaluated.
     * @param {boolean} isWhole 
     *        Indicates if the modifier applies to the whole item (true) or to a half (false).
     * @param {number} defaultPreModifierId 
     *        The preModifierId that represents a "default" modifier, used to filter defaults.
     * @returns {boolean} 
     *        Returns true if the modifier should be included in the result set; false otherwise.
     */
    private static shouldIncludeModifier(
        item: {
            modifierId: number,
            halfIndex: number,
            preModifierId: number,
            isDefault: boolean
        },
        isWhole: boolean,
        defaultPreModifierId: number
    ): boolean {
        if (item.preModifierId !== defaultPreModifierId) return true;
        if (!item.isDefault) return true;
        if (!isWhole) return true;
        return false;
    }

    /**
     * Gets the pre-modifier prefix for a modifier, if applicable.
     *
     * @param {number} preModifierId - The modifier to process.
     * @param {Data} data - Shared data object.
     * @param {LocalizationService} localizationService - Context object.
     * @returns {string} - The pre-modifier prefix or an empty string.
     */
    public static getModifierPrefix(preModifierId: number, data: Data, localizationService: LocalizationService): string {
        const defaultPreModifierId = data.getProperty("DefaultPreModifierId");
        const preModifiersData = data.getProperty("PreModifiers");

        if (preModifierId === defaultPreModifierId)
            return "";

        const preModifier = OnlineOrderingUtil.GetPreMod(preModifierId, preModifiersData);
        if (!preModifier)
            return "";

        return localizationService.GetName(preModifier.Names);
    }

    public static getAverageItemCalories(mItem: IDataItem, modifiers: IOrderItemModifier[] | null, modifiers2: IOrderItemModifier[] | null, orderTypeId: number, sizeId: number | null, defaultPreModifierId: number, defaultSizeId: number): number {
        if (sizeId == null) {
            const sizes = OnlineOrderingUtil.GetItemSizes(mItem, orderTypeId);
            if (sizes?.length === 1 && sizes[0].SizeId === 0)
                sizeId = sizes[0].SizeId;
            else
                sizeId = OnlineOrderingUtil.GetItemDefaultSize(mItem, orderTypeId, null, defaultSizeId).SizeId;
        }

        let modCals = 0;
        if (!modifiers && !modifiers2)
            modCals = OnlineOrderingUtil.getMenuItemModifiersCalories(mItem, sizeId, defaultPreModifierId);
        else if (modifiers && !modifiers2)
            modCals = OnlineOrderingUtil.getOrderItemModifiersCalories(mItem, modifiers, sizeId, defaultPreModifierId);
        else if (modifiers && modifiers2) {
            let caloriesFirstHalf = OnlineOrderingUtil.getOrderItemModifiersCalories(mItem, modifiers, sizeId, defaultPreModifierId);
            let caloriesSecondHalf = OnlineOrderingUtil.getOrderItemModifiersCalories(mItem, modifiers2, sizeId, defaultPreModifierId);
            modCals = Math.ceil((caloriesFirstHalf + caloriesSecondHalf) / 2);
        }

        return modCals;
    }

    public static getItemCaloriesNew(localizationService: LocalizationService, mItem: IDataItem, modifiers: IOrderItemModifier[] | null, modifiers2: IOrderItemModifier[] | null, orderTypeId: number, sizeId: number | null, defaultPreModifierId: number, defaultSizeId: number, languageCode: string): IName[] {
        const servingName = (mItem.ServingNames) ? mItem.ServingNames : null;
        const calories = OnlineOrderingUtil.getAverageItemCalories(mItem, modifiers, modifiers2, orderTypeId, sizeId, defaultPreModifierId, defaultSizeId);
        const serveCals = OnlineOrderingUtil.getItemSizeMinMaxCalories(mItem, sizeId);

        let calorieObj: ICalorie | null = null;
        if (serveCals.minCal !== 0 && serveCals.maxCal !== 0 && serveCals.minCal !== serveCals.maxCal) {
            if (mItem.CalorieDisplayId == 1) // per serving
                calorieObj = {
                    Name: "CalorieServingMinMax",
                    MinCal: Math.trunc((serveCals.minCal + calories) / serveCals.serves),
                    MaxCal: Math.trunc((serveCals.maxCal + calories) / serveCals.serves),
                    Serving: null,
                    ServingName: servingName
                };
            else if (mItem.CalorieDisplayId == 2) // fraction 
                calorieObj = {
                    Name: "CalorieFractionMinMax",
                    MinCal: Math.trunc((serveCals.minCal + calories) / serveCals.serves),
                    MaxCal: Math.trunc((serveCals.maxCal + calories) / serveCals.serves),
                    Serving: serveCals.serves,
                    ServingName: servingName
                };
        } else if (serveCals.minCal + calories > 0) {
            if (mItem.CalorieDisplayId == 1) // per serving
                calorieObj = {
                    Name: "CalorieServingMin",
                    MinCal: Math.trunc((serveCals.minCal + calories) / serveCals.serves),
                    MaxCal: null,
                    Serving: null,
                    ServingName: servingName
                };
            else if (mItem.CalorieDisplayId == 2) // fraction 
                calorieObj = {
                    Name: "CalorieFractionMin",
                    MinCal: Math.trunc((serveCals.minCal + calories) / serveCals.serves),
                    MaxCal: null,
                    Serving: serveCals.serves,
                    ServingName: servingName
                };
        }

        if (calorieObj) {
            const languages = localizationService.getSupportedLanguages();
            const nameObj: IName[] = [];
            for (const lang of languages) {
                const serving = calorieObj.ServingName ? localizationService.GetName(calorieObj.ServingName, lang) : localizationService.names("Slice", lang);
                switch (calorieObj.Name) {
                    case "CalorieServingMinMax":
                        nameObj.push({ CULT: lang, NAM: localizationService.names(calorieObj.Name, lang).replace("??", calorieObj.MinCal.toString()).replace("???", (calorieObj.MaxCal || "").toString()).replace("????", serving) });
                        break;
                    case "CalorieFractionMinMax":
                        nameObj.push({ CULT: lang, NAM: localizationService.names(calorieObj.Name, lang).replace("??", calorieObj.MinCal.toString()).replace("???", (calorieObj.MaxCal || "").toString()).replace("????", (calorieObj.Serving || "").toString()).replace("?????", serving) });
                        break;
                    case "CalorieServingMin":
                        nameObj.push({ CULT: lang, NAM: localizationService.names(calorieObj.Name, lang).replace("??", calorieObj.MinCal.toString()).replace("???", serving) });
                        break;
                    case "CalorieFractionMin":
                        nameObj.push({ CULT: lang, NAM: localizationService.names(calorieObj.Name, lang).replace("??", calorieObj.MinCal.toString()).replace("???", (calorieObj.Serving || "").toString()).replace("????", serving) });
                        break;
                }
            }
            return nameObj;
        }

        return [];
    }
}